import { useEffect, useState } from 'react';
import { getSubdomainFromHostname } from './useSubdomain';
import { BRAND_HOST_MAP } from '../brands/types';
import { resolveBrandKey } from '../brands/registry';
import {
  resolveByHost,
  type HostResolvedEvent,
  type HostResolvedPayload,
} from '../services/customDomainService';
import {
  getPublicLandingByBrand,
  type PublicParishLandingPayload,
} from '../services/parishLandingService';
import { ZUADARIA_FALLBACK_LANDING } from '../brands/zuadariaLandingFallback';

function getPlatformHosts(): string[] {
  const raw =
    process.env.REACT_APP_PLATFORM_HOSTS ||
    'localhost,127.0.0.1,eventos.meusacramento.com,meusacramento.com,meusacramento.store';
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase().replace(/:\d+$/, ''))
    .filter(Boolean);
}

export function isLikelyCustomDomainHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/:\d+$/, '');
  if (!host) return false;
  if (BRAND_HOST_MAP[host]) return true;
  if (getSubdomainFromHostname(host)) return false;
  if (getPlatformHosts().includes(host)) return false;
  if (host === 'localhost' || host === '127.0.0.1') return false;
  return true;
}

function isLandingPayload(data: HostResolvedPayload | null): data is PublicParishLandingPayload {
  return !!data && (data as PublicParishLandingPayload).targetType === 'landing';
}

function isEventPayload(data: HostResolvedPayload | null): data is HostResolvedEvent {
  return !!data && !isLandingPayload(data) && !!(data as HostResolvedEvent).slug;
}

/**
 * Resolve Host custom: landing da org ou evento único.
 */
export function useCustomDomainHost(): {
  loading: boolean;
  landing: PublicParishLandingPayload | null;
  event: HostResolvedEvent | null;
} {
  const [loading, setLoading] = useState(() =>
    typeof window !== 'undefined' ? isLikelyCustomDomainHost(window.location.hostname) : false
  );
  const [landing, setLanding] = useState<PublicParishLandingPayload | null>(null);
  const [event, setEvent] = useState<HostResolvedEvent | null>(null);

  useEffect(() => {
    const host = window.location.hostname;
    if (!isLikelyCustomDomainHost(host)) {
      setLoading(false);
      setLanding(null);
      setEvent(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void resolveByHost(host)
      .then(async (resolved) => {
        if (cancelled) return;
        if (isLandingPayload(resolved)) {
          setLanding(resolved);
          setEvent(null);
          return;
        }
        if (isEventPayload(resolved)) {
          setEvent(resolved);
          setLanding(null);
          return;
        }

        const brandKey = resolveBrandKey({ hostname: host });
        if (brandKey === 'zuadaria') {
          const byBrand = await getPublicLandingByBrand('zuadaria');
          if (cancelled) return;
          setLanding(byBrand || ZUADARIA_FALLBACK_LANDING);
          setEvent(null);
          return;
        }

        setLanding(null);
        setEvent(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { loading, landing, event };
}

/** Compat: só evento (hosts legacy 1→1). */
export function useCustomDomainEvent(): {
  loading: boolean;
  event: HostResolvedEvent | null;
} {
  const { loading, event } = useCustomDomainHost();
  return { loading, event };
}
